/**
 * Currency utility functions for formatting prices.
 * Prices are stored as plain numbers. Changing currency only changes the sign.
 */

export const currencyMap = {
  PKR: 'Rs.',
  USD: '$',
  INR: '₹',
  AED: 'د.إ',
  GBP: '£',
  EUR: '€',
}

// Currency configuration
const CURRENCIES = {
  USD: {
    symbol: '$',
    name: 'US Dollar',
    position: 'before',
    decimalPlaces: 2
  },
  PKR: {
    symbol: 'Rs.',
    name: 'Pakistani Rupee',
    position: 'before',
    decimalPlaces: 0
  },
  EUR: {
    symbol: '€',
    name: 'Euro',
    position: 'before',
    decimalPlaces: 2
  },
  GBP: {
    symbol: '£',
    name: 'British Pound',
    position: 'before',
    decimalPlaces: 2
  },
  INR: {
    symbol: '₹',
    name: 'Indian Rupee',
    position: 'before',
    decimalPlaces: 0
  },
  AED: {
    symbol: 'د.إ',
    name: 'UAE Dirham',
    position: 'before',
    decimalPlaces: 2
  }
}

// Default currency (can be changed from admin panel or user settings)
const DEFAULT_CURRENCY = 'USD'

const UNIT_RATES = { USD: 1, PKR: 1, INR: 1, AED: 1, GBP: 1, EUR: 1 }

export const initializeExchangeRates = async () => UNIT_RATES

export const getRates = async () => UNIT_RATES

export const getRatesSync = () => UNIT_RATES

/**
 * Format currency amount with proper decimal places and thousand separators
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code
 * @returns {string} Formatted currency string with commas
 */
export const formatCurrency = (amount, currency = 'PKR') => {
  if (!amount || isNaN(amount)) return '0'

  // Round to 2 decimal places for most currencies
  const rounded = Math.round(amount * 100) / 100

  let formattedAmount

  // For PKR and INR, show no decimals if whole number
  if (currency === 'PKR' || currency === 'INR') {
    if (rounded % 1 === 0) {
      formattedAmount = rounded.toFixed(0)
    } else {
      formattedAmount = rounded.toFixed(2)
    }
  } else {
    // For other currencies, always show 2 decimals
    formattedAmount = rounded.toFixed(2)
  }

  // Add thousand separators (commas) - split by decimal point first
  const parts = formattedAmount.split('.')
  const integerPart = parts[0]
  const decimalPart = parts[1]
  
  // Add commas to integer part (every 3 digits from right)
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  
  // Combine integer and decimal parts
  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger
}

/**
 * Convert is a no-op: stored digits stay the same; only the sign changes.
 */
export const convertFromUSDSync = (amount, _targetCurrency = 'PKR', _customRates = null) => {
  const n = Number(amount)
  return Number.isFinite(n) ? n : 0
}

export const formatPriceFromUSD = (amount, currency = DEFAULT_CURRENCY) => {
  const n = Number(amount)
  const value = Number.isFinite(n) ? n : 0
  const symbol = currencyMap[currency] || currencyMap[DEFAULT_CURRENCY] || '$'
  return `${symbol} ${formatCurrency(value, currency)}`
}

export const convertPrice = (amount, targetCurrency = 'PKR', _customRates = null) => {
  const n = Number(amount)
  const value = Number.isFinite(n) ? n : 0
  const symbol = currencyMap[targetCurrency] || 'Rs.'
  return {
    amount: value,
    symbol,
    formatted: formatCurrency(value, targetCurrency),
  }
}

export const convertToPKRSync = (amount, _fromCurrency = 'USD', _customRates = null) => {
  const n = Number(amount)
  return Number.isFinite(n) ? n : 0
}

/**
 * Format a stored price with the selected currency sign.
 */
export const formatPrice = (usdAmount, currency = DEFAULT_CURRENCY, options = {}) => {
  return formatPriceFromUSD(usdAmount, currency)
}

/**
 * Format price with "from" prefix (for price ranges)
 * @param {number} amount - The stored price
 * @param {string} currency - Currency code
 * @returns {string} Formatted price with "from" prefix
 */
export const formatPriceFrom = (amount, currency = DEFAULT_CURRENCY) => {
  return `from ${formatPriceFromUSD(amount, currency)}`
}

/**
 * Get currency symbol (matches FreshaApp)
 * @param {string} currency - Currency code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currency = DEFAULT_CURRENCY) => {
  return currencyMap[currency] || currencyMap[DEFAULT_CURRENCY] || 'Rs.'
}

/**
 * Get currency name
 * @param {string} currency - Currency code
 * @returns {string} Currency name
 */
export const getCurrencyName = (currency = DEFAULT_CURRENCY) => {
  const currencyConfig = CURRENCIES[currency] || CURRENCIES[DEFAULT_CURRENCY]
  return currencyConfig.name
}

/**
 * Get all available currencies
 * @returns {object} Object with currency codes as keys
 */
export const getAvailableCurrencies = () => {
  return CURRENCIES
}

/**
 * Get currency list as array (for dropdowns)
 * @returns {array} Array of {code, symbol, name} objects
 */
export const getCurrencyList = () => {
  return Object.entries(CURRENCIES).map(([code, config]) => ({
    code,
    symbol: config.symbol,
    name: config.name
  }))
}

/**
 * Check if currency code is valid
 * @param {string} currency - Currency code to validate
 * @returns {boolean} True if valid
 */
export const isValidCurrency = (currency) => {
  return currency && CURRENCIES.hasOwnProperty(currency)
}

// Export default currency for easy access
export { DEFAULT_CURRENCY }
